import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Save, X, Search, ArrowUpDown, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToDo = ({ user }) => {
  const [todos, setTodos] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [labels, setLabels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    task: '',
    assigned_to: '',
    deadline: '',
    status: 'Pending',
    label: ''
  });
  const [newAssignee, setNewAssignee] = useState('');
  const [newLabel, setNewLabel] = useState({ name: '', color: '#3b82f6' });
  const [showLabelForm, setShowLabelForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const fetchTodos = async () => {
    try {
      const response = await axios.get(`${API}/todos`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setTodos(response.data);
    } catch (error) {
      toast.error('Failed to fetch to-dos');
    }
  };

  const fetchAssignees = async () => {
    try {
      const response = await axios.get(`${API}/todos/assignees`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setAssignees(response.data);
    } catch (error) {
      console.error('Failed to fetch assignees');
    }
  };

  const fetchLabels = async () => {
    try {
      const response = await axios.get(`${API}/todos/labels`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setLabels(response.data);
    } catch (error) {
      console.error('Failed to fetch labels');
    }
  };

  useEffect(() => {
    fetchTodos();
    fetchAssignees();
    fetchLabels();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/todos/${editingId}`, formData, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        toast.success('To-do updated');
      } else {
        await axios.post(`${API}/todos`, formData, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        toast.success('To-do created');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ task: '', assigned_to: '', deadline: '', status: 'Pending', label: '' });
      fetchTodos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this to-do?')) return;
    try {
      await axios.delete(`${API}/todos/${id}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('To-do deleted');
      fetchTodos();
    } catch (error) {
      toast.error('Failed to delete to-do');
    }
  };

  const handleEdit = (todo) => {
    setFormData({
      task: todo.task,
      assigned_to: todo.assigned_to,
      deadline: todo.deadline,
      status: todo.status,
      label: todo.label || ''
    });
    setEditingId(todo.id);
    setShowForm(true);
  };

  const addAssignee = async () => {
    if (!newAssignee.trim()) return;
    try {
      await axios.post(`${API}/todos/assignees`, { name: newAssignee }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Assignee added');
      setNewAssignee('');
      fetchAssignees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add assignee');
    }
  };

  const addLabel = async () => {
    if (!newLabel.name.trim()) return;
    try {
      await axios.post(`${API}/todos/labels`, newLabel, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Label added');
      setNewLabel({ name: '', color: '#3b82f6' });
      setShowLabelForm(false);
      fetchLabels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add label');
    }
  };

  const deleteLabel = async (name) => {
    if (!window.confirm(`Delete label "${name}"?`)) return;
    try {
      await axios.delete(`${API}/todos/labels/${name}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Label deleted');
      fetchLabels();
    } catch (error) {
      toast.error('Failed to delete label');
    }
  };

  const isAdmin = user.role === 'admin';
  const isOperator = isAdmin || user.role === 'winlife';
  const canAddTask = isOperator || user.role === 'srh';
  const canEdit = (todo) => isOperator || todo.assigned_to === user.username;

  const getLabelColor = (labelName) => {
    const label = labels.find(l => l.name === labelName);
    return label?.color || '#3b82f6';
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredAndSortedTodos = useMemo(() => {
    let result = todos;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(todo =>
        todo.task?.toLowerCase().includes(search) ||
        todo.assigned_to?.toLowerCase().includes(search) ||
        todo.status?.toLowerCase().includes(search) ||
        todo.label?.toLowerCase().includes(search)
      );
    }

    // Sort
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        // Handle dates for deadline
        if (sortConfig.key === 'deadline') {
          const aDate = new Date(aVal || '9999-12-31');
          const bDate = new Date(bVal || '9999-12-31');
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }

        // Handle strings
        const aStr = String(aVal || '').toLowerCase();
        const bStr = String(bVal || '').toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [todos, searchTerm, sortConfig]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800">To-Do List</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <Input
            icon={<Search className="w-4 h-4" />}
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64"
          />
          {canAddTask && (
            <Button onClick={() => setShowForm(!showForm)} size="sm" className="w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          )}
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Task</label>
            <input
              type="text"
              value={formData.task}
              onChange={(e) => setFormData({ ...formData, task: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Assigned To</label>
              <select
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              >
                <option value="">Select...</option>
                <option value="ADMIN">ADMIN</option>
                <option value="SRH">SRH</option>
                <option value="WINLIFE">WINLIFE</option>
                {assignees.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Deadline</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center justify-between">
                <span>Label (Optional)</span>
                {isOperator && (
                  <Button 
                    type="button"
                    onClick={() => setShowLabelForm(!showLabelForm)} 
                    size="sm" 
                    variant="ghost"
                    className="text-xs"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {showLabelForm ? 'Hide Labels' : 'Manage Labels'}
                  </Button>
                )}
              </label>
              
              {showLabelForm && isOperator && (
                <div className="mb-2 p-3 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newLabel.name}
                      onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
                      placeholder="Label name"
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm"
                      maxLength={30}
                    />
                    <input
                      type="color"
                      value={newLabel.color}
                      onChange={(e) => setNewLabel({ ...newLabel, color: e.target.value })}
                      className="w-10 h-8 border border-slate-300 rounded"
                    />
                    <Button 
                      type="button"
                      onClick={addLabel} 
                      size="sm" 
                      disabled={labels.length >= 100}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="text-xs text-slate-600 mb-1">
                    Existing Labels ({labels.length}/100):
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                    {labels.map(label => (
                      <div
                        key={label.name}
                        className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-white"
                        style={{ backgroundColor: label.color }}
                      >
                        <span>{label.name}</span>
                        <button
                          type="button"
                          onClick={() => deleteLabel(label.name)}
                          className="hover:bg-black/20 rounded-full p-0.5"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <select
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">No Label</option>
                {labels.map(label => (
                  <option key={label.name} value={label.name}>{label.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="submit" size="sm">
              <Save className="w-4 h-4 mr-2" />
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setShowLabelForm(false);
                setFormData({ task: '', assigned_to: '', deadline: '', status: 'Pending', label: '' });
              }}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      )}

      {isOperator && (
        <div className="bg-white rounded-lg shadow-md p-4">
          {/* Manage Assignees */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Manage Assignees</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={newAssignee}
                onChange={(e) => setNewAssignee(e.target.value)}
                placeholder="Add new assignee"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
              <Button onClick={addAssignee} size="sm">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Scrollable Table View */}
        <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                <button
                  onClick={() => handleSort('task')}
                  className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                >
                  Task
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                <button
                  onClick={() => handleSort('assigned_to')}
                  className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                >
                  Assigned To
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                <button
                  onClick={() => handleSort('deadline')}
                  className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                >
                  Deadline
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                >
                  Status
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                <button
                  onClick={() => handleSort('label')}
                  className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                >
                  Label
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedTodos.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                  No tasks found
                </td>
              </tr>
            ) : (
              filteredAndSortedTodos.map((todo) => (
                <tr key={todo.id} className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm text-slate-800">{todo.task}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{todo.assigned_to}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{todo.deadline}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      todo.status === 'Completed' ? 'bg-green-100 text-green-700' :
                      todo.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {todo.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {todo.label ? (
                      <span
                        className="px-2 py-1 text-xs rounded text-white"
                        style={{ backgroundColor: getLabelColor(todo.label) }}
                      >
                        {todo.label}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {canEdit(todo) && (
                        <Button onClick={() => handleEdit(todo)} size="sm" variant="ghost">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {isOperator && (
                        <Button onClick={() => handleDelete(todo.id)} size="sm" variant="ghost">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        
        {filteredAndSortedTodos.length > 0 && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm text-slate-600">
            Showing {filteredAndSortedTodos.length} of {todos.length} tasks
          </div>
        )}
      </div>
    </div>
  );
};

export default ToDo;
